/**
 * Checkout bridge — Stripe stub.
 *
 * Two modes, switched by `VITE_CHECKOUT_MODE`:
 *
 * - `"mock"` (default): swallows the cmd, simulates a 1.5s round-trip, then
 *   sends `checkoutCompletedFromBridge` back to Elm. No network. Free to demo
 *   on staging without a Stripe account.
 *
 * - `"stripe"`: calls the `/api/create-checkout-session` serverless route with
 *   tier + referrer + userId; receives a `{ sessionId }`; redirects to
 *   Stripe-hosted Checkout via `stripe.redirectToCheckout({ sessionId })`. The
 *   Stripe webhook (`/api/stripe-webhook`) mints the entitlement server-side,
 *   the user is redirected back to `/app/checkout/success`, and Clerk's
 *   `publicMetadata` already carries the entitlement on next session refresh.
 *
 * The Elm-facing port shape (`CheckoutCommand` in/`CheckoutCompletedPayload`
 * out) is identical between modes — flipping mode never touches Elm.
 *
 * Wire-up: import `wireCheckoutBridge` in `main.ts` after `Elm.Main.init` and
 * pass it `app.ports`. Like the other bridges, it tolerates Elm's DCE — if
 * neither `checkoutCmd` nor `checkoutCompletedFromBridge` exist, it no-ops.
 */

import type { Elm as ElmModule } from "*.elm";

type ElmPorts = ReturnType<typeof getPorts>;

// Helper: extract the ports type without re-declaring it.
const getPorts = (
  app: ReturnType<typeof ElmModule.Main.init>,
): typeof app.ports => app.ports;

type CheckoutMode = "mock" | "stripe";

type Tier = "monthly" | "annual" | "lifetime";

type CreateSessionRequest = {
  readonly tier: Tier;
  readonly referrer: string | null;
  readonly userId: string | null;
  readonly successUrl: string;
  readonly cancelUrl: string;
};

type CreateSessionResponse = {
  readonly sessionId: string;
};

export const wireCheckoutBridge = (ports: ElmPorts): void => {
  if (ports.checkoutCmd === undefined) return;

  // Real-money mode requires BOTH the explicit env flag and a publishable key
  // — anything else is a stub and must never grant entitlement.
  const mode: CheckoutMode =
    import.meta.env.VITE_CHECKOUT_MODE === "stripe" &&
    (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "") !== ""
      ? "stripe"
      : "mock";

  ports.checkoutCmd.subscribe((cmd) => {
    if (mode === "mock") {
      // Mock mode = paid content is hard-blocked. The bridge intentionally
      // does NOT call checkoutCompletedFromBridge — the Elm Paywall surfaces
      // a "Coming soon" state (driven by flags.checkoutMode), and the
      // Subscribe buttons aren't even rendered. This subscribe handler is
      // wired only so the cmd doesn't dangle unobserved.
      // eslint-disable-next-line no-console
      console.info(
        "[checkout-bridge] mock mode — refusing to grant",
        cmd.tier,
      );
    } else {
      void runStripeCheckout(cmd).catch((error: unknown) => {
        // Stripe failure: surface to Sentry and let the paywall stay in
        // Processing forever (user can dismiss). A future improvement is a
        // dedicated `checkoutFailed` port that flips back to NotStarted.
        // eslint-disable-next-line no-console
        console.error("[checkout-bridge] stripe checkout failed", error);
      });
    }
  });
};

const runStripeCheckout = async (cmd: {
  readonly tier: Tier;
  readonly referrer: string | null;
  readonly userId: string | null;
}): Promise<void> => {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  if (publishableKey === undefined || publishableKey === "") {
    throw new Error(
      "VITE_STRIPE_PUBLISHABLE_KEY missing — cannot run stripe checkout",
    );
  }

  const body: CreateSessionRequest = {
    tier: cmd.tier,
    referrer: cmd.referrer,
    userId: cmd.userId,
    successUrl: `${globalThis.location.origin}/app/checkout/success`,
    cancelUrl: `${globalThis.location.origin}/app`,
  };

  const response = await fetch("/api/create-checkout-session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`create-checkout-session ${response.status.toString()}`);
  }

  const json = (await response.json()) as unknown;
  const session = parseSessionResponse(json);

  // Lazy-load @stripe/stripe-js so the SDK only ships when checkout is
  // actually invoked. Saves ~40 KB gz on the marketing surface.
  const { loadStripe } = await import("@stripe/stripe-js");
  const stripe = await loadStripe(publishableKey);
  if (stripe === null) {
    throw new Error("loadStripe returned null");
  }

  const { error } = await stripe.redirectToCheckout({
    sessionId: session.sessionId,
  });
  if (error !== undefined) {
    throw new Error(error.message ?? "stripe.redirectToCheckout error");
  }
};

const parseSessionResponse = (raw: unknown): CreateSessionResponse => {
  if (
    typeof raw === "object" &&
    raw !== null &&
    "sessionId" in raw &&
    typeof (raw as { sessionId: unknown }).sessionId === "string"
  ) {
    return { sessionId: (raw as { sessionId: string }).sessionId };
  }
  throw new Error("create-checkout-session returned malformed payload");
};
